<p-toast></p-toast>

<kanzi-ui-main-card
  [title]="'stores.edit.title' | translate"
  description="{{ 'stores.edit.description' | translate }}"
>
  <ng-template kTemplate="rightTopContent">
    <div class="flex gap-2">
      <p-button
        label="{{ 'common.cancel' | translate }}"
        icon="pi pi-times"
        severity="primary"
        [outlined]="true"
        (onClick)="onCancel()"
        [disabled]="updateMutation.isPending()"
      ></p-button>

      <p-button
        label="{{ 'common.save' | translate }}"
        icon="pi pi-save"
        severity="primary"
        (onClick)="onSave()"
        [loading]="updateMutation.isPending()"
        [disabled]="isLoading() || hasValidationErrors() || updateMutation.isPending()"
      ></p-button>
    </div>
  </ng-template>

  <!-- Loading State -->
  @if (isLoading()) {
    <div class="content-container">
      <p-skeleton width="100%" height="50px" styleClass="mb-3"></p-skeleton>
      <p-skeleton width="100%" height="50px" styleClass="mb-3"></p-skeleton>
      <p-skeleton width="100%" height="100px" styleClass="mb-3"></p-skeleton>
    </div>
  }

  <!-- Form -->
  @if (!isLoading()) {
    <div class="content-container">
      <form class="grid formgrid p-fluid">
        <!-- Nombre -->
        <div class="field col-12 md:col-6">
          <label for="name" class="font-semibold">
            {{ 'stores.form.name' | translate }}
            <span class="text-red-500">*</span>
          </label>
          <input
            pInputText
            id="name"
            [(ngModel)]="store.name"
            name="name"
            [placeholder]="'stores.form.namePlaceholder' | translate"
            required
          />
        </div>

        <!-- External ID -->
        <div class="field col-12 md:col-6">
          <label for="external_id" class="font-semibold">
            {{ 'stores.form.externalId' | translate }}
          </label>
          <input
            pInputText
            id="external_id"
            [(ngModel)]="store.external_id"
            name="external_id"
            [placeholder]="'stores.form.externalIdPlaceholder' | translate"
          />
        </div>

        <!-- Descripción -->
        <div class="field col-12">
          <label for="description" class="font-semibold">
            {{ 'stores.form.description' | translate }}
          </label>
          <textarea
            pInputTextarea
            id="description"
            [(ngModel)]="store.description"
            name="description"
            rows="3"
            [placeholder]="'stores.form.descriptionPlaceholder' | translate"
          ></textarea>
        </div>

        <!-- Customer Multi-Select (shown when logged user's customer has no parent) -->
        @if (showCustomerSelector()) {
          <div class="field col-12">
            <label for="customer_ids" class="font-semibold">
              {{ 'warehouses.form.customers' | translate }}
              <span class="text-red-500">*</span>
            </label>
            <p-multiSelect
              inputId="customer_ids"
              [(ngModel)]="selectedCustomerIds"
              [options]="customersQuery.data()?.results || []"
              optionLabel="name"
              optionValue="id"
              [placeholder]="'warehouses.form.customersPlaceholder' | translate"
              [filter]="true"
              filterBy="name"
              [loading]="customersQuery.isLoading()"
              display="chip"
              name="customer_ids"
            ></p-multiSelect>
          </div>
        }

        <!-- Geographic Information Section -->
        <div class="field col-12">
          <h3 class="text-xl font-semibold mb-3">
            {{ 'location.geographic_info' | translate }}
          </h3>
        </div>

        <!-- Country -->
        <div class="field col-12 md:col-6">
          <label for="country_id" class="font-semibold">
            {{ 'location.country' | translate }}
          </label>
          <p-dropdown
            inputId="country_id"
            [(ngModel)]="store.country_id"
            [options]="countriesQuery.data()?.results || []"
            optionLabel="name"
            optionValue="id"
            [placeholder]="'location.countryPlaceholder' | translate"
            [filter]="true"
            filterBy="name"
            [showClear]="true"
            (onChange)="onCountryChange()"
            [loading]="countriesQuery.isLoading()"
            name="country_id"
          ></p-dropdown>
        </div>

        <!-- State -->
        <div class="field col-12 md:col-6">
          <label for="state_id" class="font-semibold">
            {{ 'location.state' | translate }}
          </label>
          <p-dropdown
            inputId="state_id"
            [(ngModel)]="store.state_id"
            [options]="statesQuery.data()?.results || []"
            optionLabel="name"
            optionValue="id"
            [placeholder]="'location.statePlaceholder' | translate"
            [filter]="true"
            filterBy="name"
            [showClear]="true"
            [disabled]="!store.country_id"
            (onChange)="onStateChange()"
            [loading]="statesQuery.isLoading()"
            name="state_id"
          ></p-dropdown>
        </div>

        <!-- City -->
        <div class="field col-12 md:col-6">
          <label for="city_id" class="font-semibold">
            {{ 'location.city' | translate }}
          </label>
          <p-dropdown
            inputId="city_id"
            [(ngModel)]="store.city_id"
            [options]="citiesQuery.data()?.results || []"
            optionLabel="name"
            optionValue="id"
            [placeholder]="'location.cityPlaceholder' | translate"
            [filter]="true"
            filterBy="name"
            [showClear]="true"
            [disabled]="!store.state_id"
            [loading]="citiesQuery.isLoading()"
            name="city_id"
          ></p-dropdown>
        </div>

        <!-- Postal Code -->
        <div class="field col-12 md:col-6">
          <label for="postal_code" class="font-semibold">
            {{ 'location.postal_code' | translate }}
          </label>
          <input
            pInputText
            id="postal_code"
            [(ngModel)]="store.postal_code"
            name="postal_code"
            maxlength="10"
            [placeholder]="'location.postalCodePlaceholder' | translate"
            (ngModelChange)="validatePostalCode()"
            [class.ng-invalid]="postalCodeError"
            [class.ng-dirty]="postalCodeError"
          />
          @if (postalCodeError) {
            <small class="p-error">{{ postalCodeError }}</small>
          }
        </div>

        <!-- Address -->
        <div class="field col-12">
          <label for="address" class="font-semibold">
            {{ 'location.address' | translate }}
          </label>
          <input
            pInputText
            id="address"
            [(ngModel)]="store.address"
            name="address"
            maxlength="255"
            [placeholder]="'location.addressPlaceholder' | translate"
          />
        </div>

        <!-- Contact Information Section -->
        <div class="field col-12">
          <h3 class="text-xl font-semibold mb-3">
            {{ 'location.contact_info' | translate }}
          </h3>
        </div>

        <div class="field col-12 md:col-6">
          <label for="phone" class="font-semibold">
            {{ 'location.phone' | translate }}
          </label>
          <input
            pInputText
            id="phone"
            [(ngModel)]="store.phone"
            name="phone"
            [placeholder]="'location.phonePlaceholder' | translate"
            [class.ng-invalid]="phoneError"
            [class.ng-dirty]="phoneError"
            (ngModelChange)="validatePhone()"
          />
          @if (phoneError) {
            <small class="p-error">{{ phoneError }}</small>
          }
        </div>

        <div class="field col-12 md:col-6">
          <label for="mobile" class="font-semibold">
            {{ 'location.mobile' | translate }}
          </label>
          <input
            pInputText
            id="mobile"
            [(ngModel)]="store.mobile"
            name="mobile"
            [placeholder]="'location.mobilePlaceholder' | translate"
            [class.ng-invalid]="mobileError"
            [class.ng-dirty]="mobileError"
            (ngModelChange)="validateMobile()"
          />
          @if (mobileError) {
            <small class="p-error">{{ mobileError }}</small>
          }
        </div>

        <!-- Operation Schedule Section -->
        <div class="field col-12">
          <h3 class="text-xl font-semibold mb-3">
            {{ 'location.schedule_info' | translate }}
          </h3>
        </div>

        <div class="field col-12">
          <kanzi-apes-operation-schedule
            [operatingHours]="store.operating_hours ?? null"
            [disabled]="updateMutation.isPending()"
            (operatingHoursChange)="onSchedulesChange($event)"
          ></kanzi-apes-operation-schedule>
        </div>
         <!-- Información del tipo -->
        <div class="field col-12">
          <div class="surface-100 p-3 border-round">
            <div class="flex align-items-center gap-2">
              <i class="pi pi-info-circle text-primary"></i>
              <span class="font-semibold">
                {{ 'stores.form.typeInfo' | translate }}
              </span>
            </div>
            <p class="mt-2 mb-0 text-sm text-color-secondary">
              {{ 'stores.form.typeInfoMessage' | translate }}
            </p>
          </div>
        </div>
      </form>
    </div>
  }
</kanzi-ui-main-card>
